
 // src/pages/AddStock.jsx
import React, { useState, useEffect, useCallback } from "react";
import axios from "../../axios";
import API_BASE_URL from "../../config";
import { useNavigate } from "react-router-dom";
import { Switch } from "../../components/ui/switch";

import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  IconButton,
  Chip,
  Dialog,
  DialogContent,
  CircularProgress,
  Tooltip,
  Divider,
} from "@mui/material";

import {
  Warehouse as WarehouseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
} from "@mui/icons-material";

export default function AddStock() {
  const navigate = useNavigate();
  const companyId = parseInt(localStorage.getItem("companyId"), 10);

  const [formData, setFormData] = useState({
    name: "",
    manager: "",
    notes: "",
    branchId: "",
    isActive: true,
  });

  const [branches, setBranches] = useState([]);
  const [stocks, setStocks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  const [successModal, setSuccessModal] = useState(false);

  // تحميل المستودعات
  const fetchStocks = useCallback(async () => {
    if (!companyId) return;

    try {
      const res = await axios.get(
        `${API_BASE_URL}/Stock/by-company/${companyId}`
      );
      setStocks(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error("خطأ تحميل المستودعات:", err);
    }
  }, [companyId]);

  // تحميل الفروع والمستودعات
  useEffect(() => {
    let mounted = true;

    const loadData = async () => {
      if (!companyId) {
        setLoadingData(false);
        return;
      }

      setLoadingData(true);

      try {
        const [branchesRes, stocksRes] = await Promise.all([
          axios.get(`${API_BASE_URL}/Branch/by-company/${companyId}`),
          axios.get(`${API_BASE_URL}/Stock/by-company/${companyId}`),
        ]);

        if (!mounted) return;

        const branchList = Array.isArray(branchesRes.data)
          ? branchesRes.data
          : [];

        setBranches(branchList);
        setStocks(Array.isArray(stocksRes.data) ? stocksRes.data : []);

        setFormData((prev) => ({
          ...prev,
          branchId: prev.branchId || branchList[0]?.id || "",
        }));
      } catch (err) {
        console.error("خطأ في تحميل بيانات المستودعات:", err);
      } finally {
        if (mounted) setLoadingData(false);
      }
    };

    loadData();

    return () => {
      mounted = false;
    };
  }, [companyId]);

  // تغيير حقول النموذج
  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // تغيير حالة المستودع
  const handleSwitchChange = (checked) => {
    setFormData((prev) => ({
      ...prev,
      isActive: checked,
    }));
  };

  // إضافة مستودع
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!companyId) {
      alert("تعذر تحديد الشركة الحالية.");
      return;
    }

    if (!formData.branchId) {
      alert("يرجى اختيار الفرع.");
      return;
    }

    setLoading(true);

    try {
      const payload = {
        ...formData,
        companyId,
        branchId: parseInt(formData.branchId, 10),
      };

      await axios.post(`${API_BASE_URL}/Stock`, payload);

      setSuccessModal(true);

      setFormData({
        name: "",
        manager: "",
        notes: "",
        branchId: branches[0]?.id || "",
        isActive: true,
      });

      await fetchStocks();
    } catch (err) {
      alert(
        err.response?.data?.message ||
          "حدث خطأ أثناء حفظ المستودع."
      );
    } finally {
      setLoading(false);
    }
  };

  // حذف أو تعطيل مستودع
  const handleDelete = async (id) => {
    if (!window.confirm("هل تريد حذف (تعطيل) هذا المستودع؟")) {
      return;
    }

    try {
      await axios.delete(`${API_BASE_URL}/Stock/${id}`);
      await fetchStocks();
    } catch (err) {
      alert(
        err.response?.data?.message ||
          "حدث خطأ أثناء حذف المستودع."
      );
    }
  };

  return (
    <Container
      maxWidth="xl"
      dir="rtl"
      sx={{
        mt: 2,
        mb: 3,
        px: { xs: 1, sm: 2 },
      }}
    >
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>

        {/* ترويسة مختصرة تعتمد على الثيم */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.2,
            px: { xs: 1, sm: 2 },
            py: 1.5,
            borderBottom: 1,
            borderColor: "divider",
          }}
        >
          <WarehouseIcon
            sx={{
              fontSize: 26,
              color: "primary.main",
            }}
          />

          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              color: "text.primary",
              fontSize: { xs: "1rem", sm: "1.2rem" },
            }}
          >
            إضافة مستودع
          </Typography>
        </Box>

        {/* نموذج الإضافة */}
        <Paper
          component="form"
          onSubmit={handleSubmit}
          elevation={0}
          sx={{
            p: { xs: 2, sm: 3 },
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
          }}
        >
          <Typography
            variant="subtitle1"
            sx={{
              mb: 2,
              fontWeight: 800,
              color: "text.primary",
            }}
          >
            بيانات المستودع
          </Typography>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                required
                label="اسم المستودع"
                name="name"
                value={formData.name}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                required
                label="اسم المسؤول"
                name="manager"
                value={formData.manager}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <FormControl fullWidth required>
                <InputLabel id="stock-branch-label">
                  الفرع
                </InputLabel>

                <Select
                  labelId="stock-branch-label"
                  name="branchId"
                  value={formData.branchId}
                  label="الفرع"
                  onChange={handleChange}
                >
                  {branches.map((branch) => (
                    <MenuItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                label="ملاحظات"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
              />
            </Grid>

            <Grid item xs={12}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <Switch
                  checked={formData.isActive}
                  onCheckedChange={handleSwitchChange}
                />

                <Typography variant="body2" color="text.primary">
                  تفعيل المستودع
                </Typography>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                fullWidth
                sx={{
                  py: 1.2,
                  borderRadius: 2,
                }}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "حفظ المستودع"
                )}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* قائمة المستودعات */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 3 },
            border: 1,
            borderColor: "divider",
            borderRadius: 2,
            overflow: "hidden",
          }}
        >
          <Typography
            variant="h6"
            sx={{
              mb: 2,
              fontWeight: 800,
              fontSize: { xs: "1rem", sm: "1.15rem" },
            }}
          >
            قائمة المستودعات
          </Typography>

          <Divider sx={{ mb: 2 }} />

          {loadingData ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                py: 4,
              }}
            >
              <CircularProgress size={30} />
            </Box>
          ) : (
            <Box sx={{ width: "100%", overflowX: "auto" }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "action.hover" }}>
                    {[
                      "#",
                      "الاسم",
                      "المسؤول",
                      "الفرع",
                      "الملاحظات",
                      "الحالة",
                      "الإجراءات",
                    ].map((heading) => (
                      <TableCell
                        key={heading}
                        align="center"
                        sx={{
                          fontWeight: 800,
                          color: "text.primary",
                          borderColor: "divider",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {heading}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {stocks.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={7}
                        align="center"
                        sx={{ py: 4, color: "text.secondary" }}
                      >
                        لا توجد مستودعات مسجلة
                      </TableCell>
                    </TableRow>
                  ) : (
                    stocks.map((stock, index) => (
                      <TableRow
                        key={stock.id}
                        hover
                        sx={{
                          "&:last-child td": { borderBottom: 0 },
                        }}
                      >
                        <TableCell align="center">
                          {index + 1}
                        </TableCell>

                        <TableCell sx={{ fontWeight: 700 }}>
                          {stock.name}
                        </TableCell>

                        <TableCell>
                          {stock.manager || "-"}
                        </TableCell>

                        <TableCell align="center">
                          {stock.branchName || "-"}
                        </TableCell>

                        <TableCell>
                          {stock.notes || "-"}
                        </TableCell>

                        <TableCell align="center">
                          <Chip
                            size="small"
                            label={stock.isActive ? "مفعل" : "معطل"}
                            color={stock.isActive ? "success" : "default"}
                            variant="outlined"
                          />
                        </TableCell>

                        <TableCell align="center">
                          <Tooltip title="تعديل المستودع">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() =>
                                navigate(`/edit-stock/${stock.id}`)
                              }
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          <Tooltip title="حذف أو تعطيل المستودع">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(stock.id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </Box>
          )}
        </Paper>
      </Box>

      {/* نافذة نجاح الحفظ */}
      <Dialog
        open={successModal}
        onClose={() => setSuccessModal(false)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 2,
            width: "100%",
            maxWidth: 350,
            textAlign: "center",
          },
        }}
      >
        <DialogContent>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: 2,
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                border: 2,
                borderColor: "success.main",
                color: "success.main",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 40 }} />
            </Box>

            <Typography variant="h6" fontWeight={800}>
              تم الحفظ بنجاح
            </Typography>

            <Button
              variant="contained"
              onClick={() => setSuccessModal(false)}
            >
              حسنًا
            </Button>
          </Box>
        </DialogContent>
      </Dialog>
    </Container>
  );
}
